import { useCallback } from 'react';
import { Button, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../../components/common/Flex';
import EventModalMediaContent from '../../../../components/EventModalMediaContent';
import { useGetProductDetailsQuery } from '../../../../slices/productsApiSlice';
import AppScrollArea from '../../../../components/common/AppScrollArea';
import ModalLoading from '../../processing-order/modal/common/ModalLoading';

const ProductModal = ({ setIsOpenModal, isOpenModal, productId, productData = null, onHide }) => {
	const { isDark } = useSelector((state) => state.config);
	const navigate = useNavigate();

	// 如果有productData且没有productId，尝试从productData获取id
	const actualProductId = productId || productData?.id || productData?._id;

	const {
		data: product,
		isLoading,
		isFetching,
	} = useGetProductDetailsQuery(actualProductId, {
		skip: !actualProductId,
	});

	// 使用productData或查询到的product
	const displayProduct = product || productData;

	const handleClose = useCallback(() => {
		setIsOpenModal(false);
		if (onHide) {
			onHide();
		}
	}, [setIsOpenModal, onHide]);

	const handleViewDetail = useCallback(() => {
		if (actualProductId) {
			setIsOpenModal(false);
			setTimeout(() => {
				navigate(`/product/${actualProductId}`);
			}, 100);
		}
	}, [actualProductId, navigate, setIsOpenModal]);

	const isLoadingData = isLoading || isFetching;

	return (
		<Modal show={isOpenModal} onHide={handleClose} contentClassName='border' centered size='xl'>
			<Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className='px-x1 border-bottom-0 bg-body-tertiary'>
				{isLoadingData && !displayProduct ? (
					<Spinner className='text-primary' />
				) : (
					<Modal.Title>
						<Flex>
							<h5 className='mb-0'>
								<FontAwesomeIcon icon='box' className='text-warning me-2' />
								{displayProduct?.name || '-'}
							</h5>
						</Flex>

						{displayProduct?.productCode && (
							<p className='mb-0 fs-10 mt-1 fw-normal'>
								货号：<span className='text-primary'>{displayProduct.productCode}</span>
							</p>
						)}
					</Modal.Title>
				)}
			</Modal.Header>
			<Modal.Body className='px-x1 pb-x1 pt-1 fs-10'>
				{isLoadingData && !displayProduct ? (
					<ModalLoading />
				) : displayProduct ? (
					<AppScrollArea maxHeight='calc(90vh - 10rem)'>
					<>
						<Row>
							<Col>
								<EventModalMediaContent icon='align-left' heading='基本信息'>
									<div className='fs-10'>
										{displayProduct?.name && (
											<p className='mb-2'>
												<strong>产品名称：</strong>
												{displayProduct.name}
											</p>
										)}
										{displayProduct?.productCode && (
											<p className='mb-2'>
												<strong>货号：</strong>
												{displayProduct.productCode}
											</p>
										)}
										{displayProduct?.brand && (
											<p className='mb-2'>
												<strong>品牌：</strong>
												{typeof displayProduct.brand === 'object' ? displayProduct.brand.name || '-' : displayProduct.brand}
											</p>
										)}
										{displayProduct?.category && (
											<p className='mb-2'>
												<strong>种类：</strong>
												{displayProduct.category}
											</p>
										)}
										{displayProduct?.supplier?.name && (
											<p className='mb-2'>
												<strong>供应商：</strong>
												{displayProduct.supplier.name}
											</p>
										)}
									</div>
								</EventModalMediaContent>
							</Col>
							<Col sm={7}>
								<EventModalMediaContent icon='dollar-sign' heading='价格信息'>
									<div className='fs-10'>
										{displayProduct?.price !== undefined && (
											<p className='mb-2'>
												<strong>价格：</strong>
												<span className='text-primary fw-bold'>¥{displayProduct.price?.toFixed(2) || '0.00'}</span>
											</p>
										)}
										{displayProduct?.minimumSellingPrice !== undefined && (
											<p className='mb-2'>
												<strong>最低售价：</strong>¥{displayProduct.minimumSellingPrice?.toFixed(2) || '0.00'}
											</p>
										)}
										{displayProduct?.purchasingPrice !== undefined && (
											<p className='mb-2'>
												<strong>进价：</strong>¥{displayProduct.purchasingPrice?.toFixed(2) || '0.00'}
											</p>
										)}
									</div>
								</EventModalMediaContent>
							</Col>
						</Row>
						{displayProduct?.description && (
							<Row className='mt-3'>
								<Col>
									<EventModalMediaContent icon='align-left' heading='产品描述'>
										<div className='fs-10'>
											<p className='mb-0'>{displayProduct.description}</p>
										</div>
									</EventModalMediaContent>
								</Col>
							</Row>
						)}
						{displayProduct?.image && (
							<Row className='mt-3'>
								<Col>
									<EventModalMediaContent icon='image' heading='产品图片'>
										<div className='text-center'>
											<img src={displayProduct.image} alt={displayProduct.name} style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
										</div>
									</EventModalMediaContent>
								</Col>
							</Row>
						)}
					</>
					</AppScrollArea>
				) : (
					<div className='text-center py-4'>
						<p className='text-muted'>无法加载产品信息</p>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer className='border-top-0'>
				<Button variant='falcon-default' size='sm' onClick={handleClose}>
					关闭
				</Button>
				{actualProductId && (
					<Button variant='falcon-primary' size='sm' onClick={handleViewDetail}>
						查看详情
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default ProductModal;
